import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAppContexts } from "@/hooks/use-app-context";


export const Route = createFileRoute("/_authenticated/cafe/eventos/nuevo")({
  head: () => ({ meta: [{ title: "Crear evento especial — Brulo" }] }),
  component: NewCafeEvent,
});

type ClubOption = { id: string; name: string; city: string | null };
type TipoActividad = "running" | "ciclismo" | "ambos" | "social";

function NewCafeEvent() {
  const navigate = useNavigate();
  const { active: activeCtx, ready: ctxReady } = useAppContexts();
  const ctxCafeId = ctxReady && activeCtx.kind === "cafe" ? activeCtx.id : null;
  const [clubs, setClubs] = useState<ClubOption[]>([]);
  const [cafeId, setCafeId] = useState<string | null>(null);

  const [cafeName, setCafeName] = useState<string>("");
  const [cafeLocation, setCafeLocation] = useState<string>("");
  const [selectedClubs, setSelectedClubs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    event_date: "",
    event_time: "",
    tipo_actividad: "social" as TipoActividad,
    distance_km: "",
    cupo_maximo: "",
    meeting_details: "",
  });

  useEffect(() => {
    if (!ctxReady) return;
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      // Café del contexto activo; fallback al vínculo en cafe_managers.
      let targetId = ctxCafeId;
      if (!targetId) {
        const { data: link } = await supabase
          .from("cafe_managers")
          .select("cafe_id")
          .eq("user_id", u.user.id)
          .limit(1)
          .maybeSingle();
        targetId = link?.cafe_id ?? null;
      }
      const { data: cafe } = targetId
        ? await supabase.from("cafes").select("id,name,location").eq("id", targetId).maybeSingle()
        : { data: null };
      if (!cafe) {
        toast.error("Primero completa el perfil de tu cafetería");
        navigate({ to: "/cafe/perfil" });
        return;
      }
      setCafeId(cafe.id);
      setCafeName(cafe.name);
      setCafeLocation(cafe.location);
      const { data: cls } = await supabase
        .from("clubs")
        .select("id,name,city")
        .order("name");
      setClubs(cls ?? []);
    })();
  }, [navigate, ctxReady, ctxCafeId]);


  const set = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm({ ...form, [k]: e.target.value });

  const toggleClub = (id: string) => {
    setSelectedClubs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cafeId) return;
    setLoading(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) {
      setLoading(false);
      return;
    }

    const { data: ev, error } = await supabase
      .from("events")
      .insert({
        organizer_id: u.user.id,
        organizador_tipo: "cafeteria",
        tipo_actividad: form.tipo_actividad,
        cafe_id: cafeId,
        club_id: null,
        name: form.name,
        description: form.description || null,
        event_date: form.event_date,
        event_time: form.event_time,
        meeting_point: `${cafeName} — ${cafeLocation}`,
        meeting_details: form.meeting_details || null,
        distance_km: form.distance_km ? parseFloat(form.distance_km) : null,
        cupo_maximo: form.cupo_maximo ? parseInt(form.cupo_maximo, 10) : null,
      })
      .select("id")
      .single();

    if (error || !ev) {
      setLoading(false);
      toast.error(error?.message ?? "No pudimos crear el evento");
      return;
    }

    if (selectedClubs.size > 0) {
      const invitaciones = Array.from(selectedClubs).map((club_id) => ({
        evento_id: ev.id,
        club_id,
      }));
      const { error: invErr } = await supabase
        .from("invitaciones_clubs")
        .insert(invitaciones);
      if (invErr) {
        toast.error(`Evento creado, pero falló invitar clubs: ${invErr.message}`);
      }
    }

    setLoading(false);
    toast.success("Evento creado y publicado");
    navigate({ to: "/cafe/dashboard" });
  };

  return (
    <div className="mx-auto max-w-2xl px-4 md:px-6 py-8 md:py-12">
      <p className="text-primary text-xs tracking-[0.3em] uppercase mb-2">evento del local</p>
      <h1 className="font-display text-4xl sm:text-5xl mb-2">Crear evento especial</h1>
      <p className="text-muted-foreground mb-8 md:mb-10">
        Organiza tu propio evento e invita a los clubs Brulo a participar.
      </p>

      <form onSubmit={submit} className="space-y-5 bg-card border border-border rounded-lg p-5 sm:p-8">
        <Field label="Nombre del evento">
          <input
            required
            value={form.name}
            onChange={set("name")}
            maxLength={120}
            className={inputCls}
            placeholder="Aniversario Venerdi Café"
          />
        </Field>

        <Field label="Descripción">
          <textarea
            value={form.description}
            onChange={set("description")}
            rows={3}
            maxLength={500}
            className={inputCls}
            placeholder="Celebramos 3 años con café, música y comunidad..."
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Fecha">
            <input required type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} />
          </Field>
          <Field label="Hora">
            <input required type="time" value={form.event_time} onChange={set("event_time")} className={inputCls} />
          </Field>
        </div>

        <Field label="Tipo de evento">
          <select
            required
            value={form.tipo_actividad}
            onChange={set("tipo_actividad")}
            className={inputCls}
          >
            <option value="running">Running</option>
            <option value="ciclismo">Ciclismo</option>
            <option value="ambos">Ambos (running + ciclismo)</option>
            <option value="social">Social (sin actividad deportiva)</option>
          </select>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Distancia km (opcional)">
            <input
              type="number"
              step="0.1"
              min="0"
              value={form.distance_km}
              onChange={set("distance_km")}
              className={inputCls}
              placeholder="0"
            />
          </Field>
          <Field label="Cupo máximo (opcional)">
            <input
              type="number"
              min="1"
              value={form.cupo_maximo}
              onChange={set("cupo_maximo")}
              className={inputCls}
              placeholder="50"
            />
          </Field>
        </div>

        <Field label="Detalles del punto de encuentro (opcional)">
          <input
            value={form.meeting_details}
            onChange={set("meeting_details")}
            maxLength={150}
            className={inputCls}
            placeholder="Te esperamos en la terraza"
          />
        </Field>

        <div>
          <label className="text-sm text-muted-foreground block mb-2">
            Clubs invitados ({selectedClubs.size} seleccionados)
          </label>
          {clubs.length === 0 ? (
            <p className="text-sm text-muted-foreground bg-muted/30 rounded-md p-3">
              Aún no hay clubs registrados.
            </p>
          ) : (
            <div className="max-h-64 overflow-y-auto border border-border rounded-md divide-y divide-border">
              {clubs.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-3 p-3 hover:bg-muted/40 cursor-pointer min-h-11"
                >
                  <input
                    type="checkbox"
                    checked={selectedClubs.has(c.id)}
                    onChange={() => toggleClub(c.id)}
                    className="w-4 h-4 accent-primary"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.name}</p>
                    {c.city && <p className="text-xs text-muted-foreground">{c.city}</p>}
                  </div>
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            El evento es público — cualquier corredor podrá inscribirse además de los clubs invitados.
          </p>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full min-h-12 px-3 py-3 rounded-md bg-primary text-primary-foreground text-sm sm:text-base font-semibold tracking-wide hover:opacity-90 disabled:opacity-50 whitespace-normal break-words text-center leading-tight"
        >
          {loading ? "Publicando..." : "Publicar evento e invitar clubs"}
        </button>
      </form>
    </div>
  );
}

const inputCls =
  "w-full px-4 py-3 min-h-11 bg-input border border-border rounded-md focus:outline-none focus:border-primary transition";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-sm text-muted-foreground block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
